import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, Globe, BookOpen, Heart, Building } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-900 to-green-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-4">Our Story</h1>
            <p className="text-xl text-green-100 leading-relaxed">
              Pioneering independent research in Highland Asia since 2013
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Who We Are</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                Established in <strong>2013</strong> and registered in Nagaland, India, the Highland Institute is
                a pioneering independent research centre dedicated to addressing complex socio-political, cultural,
                historical, environmental, and developmental challenges in Highland Asia and beyond.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our mission is to drive meaningful change through rigorous research and collaborative innovation,
                bridging local knowledge systems with global academic discourse.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What We Do</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Research & Innovation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    We partner with academic and governmental institutions globally to design and execute comprehensive
                    field studies, disseminating findings through symposia and peer-reviewed journals.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Educational Initiatives</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Our School of Advanced Studies trains aspiring researchers in field and archival methods, preparing
                    them for meaningful contributions to ongoing projects and academic discussions.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Heart className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Community Engagement</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    From citizen science to public exhibitions, we collaborate with local communities, schools, and
                    indigenous knowledge holders to create inclusive research environments.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Our Partnerships</h2>
            
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Globe className="w-6 h-6 text-green-600 mr-3" />
                International Collaborators
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">UiT Arctic University of Norway</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">University of Campinas, Brazil</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">University of Cambridge, UK</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">University of Edinburgh, UK</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">University of Leeds, UK</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">University of Oxford, UK</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">University of Sussex, UK</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">Danish Institute for International Studies</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Building className="w-6 h-6 text-green-600 mr-3" />
                Regional & Local Partners
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">Ambedkar University Delhi</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">RV University Bangalore</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">Universities of Assam & Nagaland</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">Centre for Karbi Studies</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">Kohima Science College</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                  <p className="font-medium text-gray-900">Government Colleges (Dimapur & Phek)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Engagement Beyond Academia</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              We actively collaborate with government departments and non-academic organizations to expand our impact:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Government of Nagaland (Art & Culture, Health & Family Welfare)</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Nagaland State Biodiversity Board</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">State Climate Change Cell</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Doctors For You</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">International Development Research Centre of Canada</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Oxford Policy Management</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Join Our Mission</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Be part of our journey in advancing research, education, and community engagement in Highland Asia
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/programmes">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Study With Us
              </Button>
            </Link>
            <Link to="/research">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Explore Research
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
